import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const faqs = [
  {
    q: "海角爆的快讯多久更新一次？",
    a: "海角爆的快讯基本全天实时跟进，热门事件进展会第一时间同步。资讯流与实时热搜榜通常每分钟刷新，遇到重大事件时更新会更密集，你无需手动刷新也能跟上最新节点。",
  },
  {
    q: "如何快速找到某个分类或话题的快讯？",
    a: "回到首页后，你可以在“分类快讯”中点击热点、娱乐、影视、综艺、科技圈或生活方式等标签进行切换；也可以直接使用搜索框输入标题或关键词（如综艺名、明星名、品牌名）快速定位相关内容。",
  },
  {
    q: "资讯里的内容都可靠吗？来源是否真实？",
    a: "我们坚持客观、谨慎的采编原则，大部分内容来自公开信息整理与用户线索核实。对于暂无法完全核实、处于传言阶段的信息，我们会如实标注，建议读者理性看待，以官方发布为准。",
  },
  {
    q: "如何订阅每日新鲜快讯推送？",
    a: "你可以在首页底部的订阅横幅中留下常用邮箱并点击“免费订阅”。订阅后我们会定期为你整理当天的重要快讯与热门事件进展。如需停止接收，随时可通过邮件中的退订链接取消，我们重视你的隐私。",
  },
  {
    q: "我有一条新鲜线索，要怎么投稿爆料？",
    a: "欢迎提供真实可靠的新鲜线索。你可以点击顶部导航中的“我要爆料”，或直接发送邮件到我们公布的官方联系邮箱，说明事件的时间、地点与已知信息。一经核实，我们会第一时间跟进发布。",
  },
  {
    q: "发现报道有误或想提出更正，应该怎么做？",
    a: "如果你发现某条快讯存在事实出入或表述不当，请通过邮件把链接与具体问题告诉我们，或使用页内“我要爆料”提交更正建议。编辑部核实后会在第一时间修正，并感谢每一位认真负责的读者。",
  },
  {
    q: "快讯太多看不过来，有没有办法只看自己感兴趣的？",
    a: "当然有。你可以使用分类标签只浏览某一类内容，也可以通过搜索精准定位。此外关注的热门事件，可借助“追更时间线”按时间梳理关键进展，帮助你不漏掉重要环节。",
  },
  {
    q: "什么是“实时热搜榜”？数据多久刷新？",
    a: "实时热搜榜呈现当前讨论热度较高的话题，基于站内内容的浏览与互动情况生成，通常在每分钟内刷新。榜单仅反映站内内容热度，供快速了解当前大家正在聊什么。",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Faq() {
  const [open, setOpen] = useState(0);

  useSeo({
    title: "常见问题 FAQ - 快讯更新、订阅与投稿指南",
    description: `关于${siteInfo.brandFull}快讯更新频率、分类浏览、实时热搜榜、邮件订阅、投稿爆料与内容更正等常见问题的解答，帮助新老读者更快上手，不错过任何新鲜快讯。`,
    keywords: "海角爆常见问题,海角爆订阅,海角爆投稿,快讯更新,实时热搜榜,海角爆怎么用",
    path: "/faq",
    type: "website",
    jsonLd: faqJsonLd,
  });

  return (
    <>
      <Navbar />
      <main>
        <section className="bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
            <nav className="flex items-center gap-1.5 text-sm text-background-100/80" aria-label="面包屑">
              <Link to="/" className="hover:text-background-50">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50">常见问题</span>
            </nav>
            <div className="mt-6 max-w-2xl">
              <h1 className="flex items-center gap-3 font-heading text-3xl font-black text-background-50 md:text-4xl">
                <i className="ri-questionnaire-line"></i> 常见问题
              </h1>
              <p className="mt-3 text-[15px] leading-relaxed text-background-100">
                关于快讯更新、分类浏览、订阅与投稿的疑问，这里都有答案。如果没找到你想要的，欢迎通过邮件告诉我们。
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 py-12 md:px-6">
          <div className="space-y-3">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className="overflow-hidden rounded-2xl bg-background-100">
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${isOpen ? "bg-primary-500 text-background-50" : "bg-accent-100 text-accent-900"}`}>
                        Q{i + 1}
                      </span>
                      <span className="font-heading text-base font-bold text-foreground-950">{f.q}</span>
                    </span>
                    <i className={`ri-add-line text-xl text-foreground-500 transition-transform ${isOpen ? "rotate-45" : ""}`}></i>
                  </button>
                  <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 pl-[60px] text-[15px] leading-7 text-foreground-700">{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 rounded-2xl bg-background-100 p-8 text-center">
            <h2 className="font-heading text-xl font-bold text-foreground-950">还有问题？我们来帮你</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">想要了解快讯更新或投稿合作等更多细节，欢迎随时联系我们，或订阅快讯不错过任何新鲜动态。</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`mailto:${siteInfo.email}`} className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
                <i className="ri-mail-send-line"></i> 联系我们
              </a>
              <Link to="/#subscribe" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-200">
                <i className="ri-notification-3-line"></i> 订阅快讯
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-200">
                <i className="ri-flashlight-line"></i> 回到首页
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}